import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import toast from "react-hot-toast";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import Select from "react-select";
import { Checkbox, FormControlLabel, Accordion, AccordionSummary, AccordionDetails, Chip, LinearProgress, Box, } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
// ─── Types ─────────────────────────────────────────────────────────────────
// "Type of Exit" — the reason the employee is exiting, distinct from
// exitStatus (which tracks the operational stage: notice period/left/etc).
const EXIT_TYPE_OPTIONS = [
    "Resignation",
    "Completion of Tenure",
    "Retirement",
    "Demise",
    "Termination",
    "Asked to Leave",
    "Absconded",
];
// Interns don't retire or get "Terminated" in the HR-process sense — only
// these 5 exit types have an Intern-specific checklist (see
// INTERN_CHECKLIST_TEMPLATES below), so the dropdown is narrowed to match
// whenever the selected employmentType is "Internship".
const INTERN_EXIT_TYPE_OPTIONS = [
    "Resignation",
    "Completion of Tenure",
    "Asked to Leave",
    "Absconded",
    "Demise",
];
// "Type of Employment" — auto-filled from the matched Onboarding record's
// employeeCategory (see mapEmployeeCategoryToEmploymentType below), shown
// here in HR-facing wording rather than Onboarding's internal enum values.
const EMPLOYMENT_TYPE_OPTIONS = [
    "Full Time Employment",
    "Contract",
    "Internship",
    "Internship with PPO",
    "Part Time",
    "Temporary Staffing",
    "Consultant",
];
const mapEmployeeCategoryToEmploymentType = (category) => {
    switch (category) {
        case "Employee":
            return "Full Time Employment";
        case "Contract Based":
            return "Contract";
        case "Intern":
            return "Internship";
        case "Intern with PPO":
            return "Internship with PPO";
        case "Part Time":
            return "Part Time";
        case "Temporary Staffing":
            return "Temporary Staffing";
        case "Consultant":
            return "Consultant";
        default:
            return "";
    }
};
// Confidential — never referenced by any email template/trigger. Shown to
// HR only when Type of Exit is "Asked to Leave".
const REASON_ASKED_TO_LEAVE_OPTIONS = [
    "Attitude (Refused to take ownership)",
    "Conduct",
    "Performance",
    "Role Redundancy",
];
const schema = z
    .object({
    name: z.string().min(1, "Employee name is required"),
    gender: z.string().min(1, "Gender is required"),
    mobile: z.string().min(1, "Mobile is required"),
    persEmail: z.string().email("Invalid email"),
    officialEmail: z.string().optional(),
    dept: z.string().min(1, "Department is required"),
    designation: z.string().min(1, "Designation is required"),
    employmentType: z.string().optional(),
    noticePeriod: z.string().min(1, "Notice period is required"),
    transferKnowledge: z.string().min(1, "Select who knowledge will be transferred to"),
    exitStatus: z.enum(["Serving Notice Period", "Already Left", "Left", "Not Exiting", "Exit Cancelled"]),
    exitType: z.string().min(1, "Type of Exit is required"),
    reasonAskedToLeave: z.string().optional(),
    reasonAskedToLeaveDetail: z.string().optional(),
    remarks: z.string().optional(),
    autoExitEmail: z.boolean().optional(),
    autoExitEmailDept: z.boolean().optional(),
    autoReminderEmail: z.boolean().optional(),
    autoInstructionsToAllEmail: z.boolean().optional(),
})
    .superRefine((data, ctx) => {
    if (data.exitType === "Asked to Leave" && !data.reasonAskedToLeave) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["reasonAskedToLeave"],
            message: "Reason is required when Type of Exit is Asked to Leave",
        });
    }
});
const API_BASE = process.env.REACT_APP_REACT_APP_API_BASE_URL || "http://localhost:5000/api";
// ─── Checklist templates (must match backend's buildDefaultCheckLists()
// exactly — same groups/items per employment type × exit type combo, same
// GENERIC fallback for combos with no specific template) ────────────────────
const GROUP_STYLES = {
    "PRE-EXIT TASKS": { color: "bg-red-50 border-red-200", accent: "#dc2626" },
    "EXIT-DAY TASKS": { color: "bg-amber-50 border-amber-200", accent: "#d97706" },
    "POST-EXIT TASKS": { color: "bg-slate-50 border-slate-200", accent: "#475569" },
    "EXIT TASKS": { color: "bg-purple-50 border-purple-200", accent: "#7c3aed" },
};
const DEFAULT_GROUP_STYLE = { color: "bg-slate-50 border-slate-200", accent: "#475569" };
const GENERIC_CHECKLIST_TEMPLATE = [
    {
        name: "PRE-EXIT TASKS",
        items: [
            "Exit Email Done?",
            "Reminder Email Done?",
            "Take a Printout of Exit Email Done?",
            "Exit Email to All Dept Cc Done?",
            "Get a Handing Over Done from Employee?",
            "Conducting Exit Interview with Mgmt Done?",
        ],
    },
    {
        name: "EXIT-DAY TASKS",
        items: [
            "Sign Exit Form Done?",
            "Sign No Dues Certificate Done?",
            "Ensure All Assets Are Returned Done?",
            "Name Deleted from Employee List Done?",
            "Tea Party Done?",
            "SIM Returned Done?",
        ],
    },
    {
        name: "POST-EXIT TASKS",
        items: [
            "Close the Contract on Odoo Done?",
            "Reassign Assets Done?",
            "Sent an Approval Mail to Mgmt Done?",
            "Issue FnF Salary Done?",
            "Issue Experience Letter Done?",
            "Reallotment of Delegation & Checklist Task Done?",
            "Remove Email from Google Drive Done?",
            "Remove Biometric Access Done?",
            "Change S2ndLife Password Done?",
            "Remove ERP Password Done?",
            "Archive Employee Profile Done?",
            "Remove the Access from Shared Contacts Done?",
            "Delete Email from BO Domain Done?",
            "Remove Employee from BO WhatsApp Gp Done?",
        ],
    },
];
const INTERN_CHECKLIST_TEMPLATES = {
    "Resignation": [
        {
            name: "PRE-EXIT TASKS",
            items: [
                "Exit Email / Resignation Received Done?",
                "Resignation Approval and Last Working Day Informed Done?",
                "Reminder Email Sent Done?",
                "Exit Email Shared with Reporting Manager Done?",
                "Handover Completed from Intern Done?",
            ],
        },
        {
            name: "EXIT-DAY TASKS",
            items: [
                "Exit / Clearance Form Signed from Intern Done?",
                "All Company Assets Returned Done?",
                "Intern Name Removed from HR Portal, Gmail, WhatsApp Done?",
            ],
        },
        {
            name: "POST-EXIT TASKS",
            items: [
                "Close Intern Contract and Remove Details Done?",
                "Stipend Processed Done?",
                "Internship Certificate Issued Done?",
                "Tasks Reassigned Done?",
                "Update Status and Archive Intern Profile Done?",
            ],
        },
    ],
    "Completion of Tenure": [
        {
            name: "PRE-EXIT TASKS",
            items: [
                "Completion of Internship Email Sent to the Reporting Manager Done?",
                "Last Working Day Informed to the Intern Done?",
                "Reminder Email Sent 15 Days Before Done?",
                "Extension of Internship, if Applicable, Confirmed from Management Done?",
                "Handover Completed from Intern Done?",
                "Pending Tasks / Projects Reviewed Done?",
            ],
        },
        {
            name: "EXIT-DAY TASKS",
            items: [
                "Exit / Clearance Form Signed from Intern Done?",
                "All Company Assets Returned Done?",
                "Intern Name Removed from HR Portal, Gmail, WhatsApp Done?",
            ],
        },
        {
            name: "POST-EXIT TASKS",
            items: [
                "Close Intern Contract and Remove Details Done?",
                "Stipend Processed Done?",
                "Internship Certificate Issued Done?",
                "Tasks Reassigned Done?",
                "Archive Employee Profile Done?",
            ],
        },
    ],
    "Asked to Leave": [
        {
            name: "PRE-EXIT TASKS",
            items: [
                "Email Sent to Intern Asking Him to Leave Done?",
                "Discussion with the Intern Done?",
                "Reporting Manager Informed Regarding the Decision Done?",
                "HR Exit Process Initiated Done?",
                "Last Working Day Informed to Intern Done?",
                "Reminder Sent to HR to Complete the Exit of Intern Done?",
                "Handover Completed from Intern Done?",
                "Management Decision to Ask Intern to Leave Approval Done?",
            ],
        },
        {
            name: "EXIT-DAY TASKS",
            items: [
                "Exit / Clearance Form Signed from Intern Done?",
                "All Company Assets Returned Done?",
                "Intern Name Removed from HR Portal, Gmail, WhatsApp Done?",
            ],
        },
        {
            name: "POST-EXIT TASKS",
            items: [
                "Close Intern Contract and Remove Details Done?",
                "Stipend Processed, if Applicable Done?",
                "Internship Certificate Issued, if Applicable Done?",
                "Tasks Reassigned Done?",
                "Archive Employee Profile Done?",
            ],
        },
    ],
    "Absconded": [
        {
            name: "PRE-EXIT TASKS",
            items: [
                "Intern Absence Identified & Reported to the Manager Done?",
                "Reminder / Warning Email Sent to the Personal Email ID Done?",
                "Absconding Notice Issued to the Intern Done?",
                "Management, Reporting Manager Informed and Decision Updated in the HR Portal Done?",
            ],
        },
        {
            name: "EXIT-DAY TASKS",
            items: [
                "Last Attendance Date Confirmed Done?",
                "Company Assets Identified & Returned Done?",
                "Pending Salary / Dues & Recovery Amount Calculated Done?",
            ],
        },
        {
            name: "POST-EXIT TASKS",
            items: [
                "Close Contract in HR Portal Done?",
                "F&F Salary / Dues Processed, if Applicable as per Policy Done?",
                "Pending Tasks Reassigned Done?",
                "Remove Intern from Biometric, WhatsApp, Gmail, or Any Other Shared Systems Done?",
                "Update in the HR Portal Done?",
            ],
        },
    ],
    "Demise": [
        {
            name: "EXIT TASKS",
            items: [
                "Information Regarding the Demise Verified Done?",
                "Management, HR, Accounts and Reporting Manager Informed Done?",
                "Emergency Contact Details Retrieved Done?",
                "Pending Salary and Dues Calculated Done?",
                "PF / Insurance / Pending Salary Till Last Working Day Processed Done?",
                "Gratuity Processed Done?",
                "Status Updated Done?",
                "Family Contacted Done?",
                "All Company Assets Returned Done?",
                "Name Removed from HR Portal, Gmail, WhatsApp Done?",
                "Close Intern Contract Done?",
                "Required Certificate Issued Done?",
                "Tasks Reassigned Done?",
                "Archive and Close Employee Profile in HR Portal Done?",
            ],
        },
    ],
};
const EMPLOYEE_CHECKLIST_TEMPLATES = {
    "Resignation": [
        {
            name: "PRE-EXIT TASKS",
            items: [
                "Resignation Approved by Reporting Manager, HR, Management Done?",
                "Resignation Approved Email Sent to the Employee Done?",
                "Last Working Day Updated in the HR Portal Done?",
                "Accounts, HR and Reporting Manager Informed Done?",
                "Handover Tasks Completed Done?",
                "Exit Interview Conducted Done?",
            ],
        },
        {
            name: "EXIT-DAY TASKS",
            items: [
                "Exit / Clearance Form Signed Done?",
                "All Company Assets Returned Done?",
                "Name Removed from HR Portal, Gmail, WhatsApp Done?",
                "Farewell Party Conducted Done?",
            ],
        },
        {
            name: "POST-EXIT TASKS",
            items: [
                "Close Contract and Remove Details Done?",
                "F&F / Dues / Gratuity / PF Processed Done?",
                "Experience Certificate Issued Done?",
                "Tasks Reassigned Done?",
                "Update Status and Archive Employee Profile Done?",
            ],
        },
    ],
    "Asked to Leave": [
        {
            name: "PRE-EXIT TASKS",
            items: [
                "Email Sent to Employee Asking Him to Leave Done?",
                "Discussion with the Employee Done?",
                "Reporting Manager Informed Regarding the Decision Done?",
                "HR Exit Process Initiated Done?",
                "Last Working Day Informed to Employee Done?",
                "Reminder Sent to HR to Complete the Exit Done?",
                "Handover Tasks Completed Done?",
                "Management Decision to Ask Employee to Leave Approval Done?",
            ],
        },
        {
            name: "EXIT-DAY TASKS",
            items: [
                "Exit / Clearance Form Signed from Employee Done?",
                "All Company Assets Returned Done?",
                "Name Removed from HR Portal, Gmail, WhatsApp Done?",
            ],
        },
        {
            name: "POST-EXIT TASKS",
            items: [
                "Close Contract and Remove Details Done?",
                "F&F / Dues / Gratuity / PF Processed Done?",
                "Experience Certificate Issued, if Applicable Done?",
                "Tasks Reassigned Done?",
                "Update Status and Archive Employee Profile Done?",
            ],
        },
    ],
    "Absconded": [
        {
            name: "PRE-EXIT TASKS",
            items: [
                "Employee Absence Identified & Reported to the Manager Done?",
                "Reminder / Warning Email Sent to the Personal Email ID Done?",
                "Absconding Notice Issued to the Employee Done?",
                "Management, Reporting Manager Informed and Decision Updated in the HR Portal Done?",
            ],
        },
        {
            name: "EXIT-DAY TASKS",
            items: [
                "Last Attendance Date Confirmed Done?",
                "Company Assets Identified & Returned Done?",
                "Pending Salary / Dues & Recovery Amount Calculated Done?",
            ],
        },
        {
            name: "POST-EXIT TASKS",
            items: [
                "Close Contract in HR Portal Done?",
                "F&F Salary / Dues Processed, if Applicable as per Policy Done?",
                "Pending Tasks Reassigned Done?",
                "Name Removed from Biometric, WhatsApp, Gmail, or Any Other Shared Systems Done?",
                "Update Status and Archive Employee Profile Done?",
            ],
        },
    ],
    "Termination": [
        {
            name: "PRE-EXIT TASKS",
            items: [
                "Termination Email Sent to Employee Done?",
                "Discussion with the Employee Done?",
                "Management Approval Completed Done?",
                "Reporting Manager Informed Regarding the Decision Done?",
                "HR Exit Process Initiated Done?",
                "Last Working Day Informed Done?",
                "Reminder Sent to HR to Complete the Exit Done?",
                "Handover Tasks Completed Done?",
            ],
        },
        {
            name: "EXIT-DAY TASKS",
            items: [
                "Exit / Clearance Form Signed Done?",
                "All Company Assets Returned Done?",
                "Name Removed from HR Portal, Gmail, WhatsApp Done?",
            ],
        },
        {
            name: "POST-EXIT TASKS",
            items: [
                "Close Contract and Remove Details Done?",
                "F&F / Dues / Gratuity / PF Processed, if Applicable Done?",
                "Experience Certificate Issued, if Applicable Done?",
                "Tasks Reassigned Done?",
                "Update Status and Archive Employee Profile Done?",
            ],
        },
    ],
    "Completion of Tenure": [
        {
            name: "PRE-EXIT TASKS",
            items: [
                "Completion of Tenure Date Confirmed Done?",
                "Completion of Employment Email Sent to Reporting Manager, HR and Management Done?",
                "Last Working Day Informed to All Done?",
                "Management Approval Done?",
                "Reminder Email Sent 15 Days Before Done?",
                "Extension / Renewal Decision Confirmed Done?",
                "Handover of Tasks Completed Done?",
                "Pending Tasks / Projects Reviewed Done?",
                "Exit / Completion Discussion Conducted Done?",
            ],
        },
        {
            name: "EXIT-DAY TASKS",
            items: [
                "Exit / Clearance Form Signed Done?",
                "All Company Assets Returned Done?",
                "Experience Certificate Released Done?",
                "Farewell Party Conducted Done?",
            ],
        },
        {
            name: "POST-EXIT TASKS",
            items: [
                "Close Contract Done?",
                "Name Removed from HR Portal, Gmail, WhatsApp Done?",
                "F&F / Dues / Gratuity / PF Processed, if Applicable Done?",
                "Tasks Reassigned Done?",
                "Update Status and Archive Employee Profile Done?",
            ],
        },
    ],
    "Retirement": [
        {
            name: "PRE-EXIT TASKS",
            items: [
                "Completion of Tenure Date Confirmed Done?",
                "Completion of Employment Email Sent to Reporting Manager, HR and Management Done?",
                "Last Working Day Informed to All Done?",
                "Management Approval Done?",
                "Reminder Email Sent 15 Days Before the Last Working Day Done?",
                "Extension / Renewal Decision Confirmed Done?",
                "Handover of Tasks Completed Done?",
                "Pending Tasks / Projects Reviewed Done?",
                "Exit / Completion Discussion Conducted Done?",
            ],
        },
        {
            name: "EXIT-DAY TASKS",
            items: [
                "Exit / Clearance Form Signed Done?",
                "All Company Assets Returned Done?",
                "Name Removed from HR Portal, Gmail, WhatsApp Done?",
                "Farewell Party Conducted Done?",
            ],
        },
        {
            name: "POST-EXIT TASKS",
            items: [
                "Close Contract and Remove Details Done?",
                "F&F / Dues / Gratuity / PF Processed, if Applicable Done?",
                "Experience Certificate Issued Done?",
                "Tasks Reassigned Done?",
                "Update Status and Archive Employee Profile Done?",
            ],
        },
    ],
    "Demise": [
        {
            name: "EXIT TASKS",
            items: [
                "Information Regarding the Demise Verified Done?",
                "Management, HR, Accounts and Reporting Manager Informed Done?",
                "Emergency Contact Details Retrieved Done?",
                "Pending Salary and Dues Calculated Done?",
                "PF / Insurance / Pending Salary Till Last Working Day Processed Done?",
                "Gratuity Processed Done?",
                "Employee Status Updated Done?",
                "Family Contacted Done?",
                "All Company Assets Returned Done?",
                "Name Removed from HR Portal, Gmail, WhatsApp Done?",
                "Close Contract Done?",
                "Required Certificate Issued Done?",
                "Tasks Reassigned Done?",
                "Archive and Close Employee Profile in HR Portal Done?",
            ],
        },
    ],
};
// startsWith (not exact match) so "Internship with PPO" gets the same
// Intern checklist/exit-type treatment as plain "Internship".
function isInternEmploymentType(employmentType) {
    return (employmentType || "").trim().toLowerCase().startsWith("internship");
}
// Picks the checklist template for the currently selected employment/exit
// type, matching backend's buildDefaultCheckLists() exactly, then attaches
// display styling per group.
function getChecklistDefs(employmentType, exitType) {
    const table = isInternEmploymentType(employmentType) ? INTERN_CHECKLIST_TEMPLATES : EMPLOYEE_CHECKLIST_TEMPLATES;
    const template = (exitType && table[exitType]) ? table[exitType] : GENERIC_CHECKLIST_TEMPLATE;
    return template.map((group) => ({
        ...(GROUP_STYLES[group.name] ?? DEFAULT_GROUP_STYLE),
        name: group.name,
        items: group.items,
    }));
}
// ─── Component ───────────────────────────────────────────────────────────────
const NewExit = () => {
    const { register, control, handleSubmit, watch, setValue, reset, formState: { errors, isSubmitting }, } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            autoExitEmail: false,
            autoExitEmailDept: false,
            autoReminderEmail: false,
            autoInstructionsToAllEmail: false,
        },
    });
    const [resignationDate, setResignationDate] = useState(null);
    const [plannedExitDate, setPlannedExitDate] = useState(null);
    const [leftDate, setLeftDate] = useState(null);
    const [joiningDate, setJoiningDate] = useState(null);
    const [employeesInCc, setEmployeesInCc] = useState([]);
    const [master, setMaster] = useState({ employees: [] });
    const [deptDesig, setDeptDesig] = useState({
        departments: [],
        designations: [],
    });
    const [checkStates, setCheckStates] = useState([]);
    // Thank-you screen: shows a brief loader, then a confirmation message —
    // same pattern already used on New Onboarding, so the form doesn't just
    // silently reset with only a toast to notice.
    const [showThankYou, setShowThankYou] = useState(false);
    const [thankYouLoading, setThankYouLoading] = useState(true);
    const exitStatus = watch("exitStatus");
    const exitType = watch("exitType");
    const employmentType = watch("employmentType");
    const selectedDept = watch("dept");
    const selectedName = watch("name");
    const selectedDesignation = watch("designation");
    // Interns only get 5 exit types (no Retirement/Termination) — narrow the
    // dropdown accordingly.
    const exitTypeOptions = useMemo(() => (isInternEmploymentType(employmentType) ? INTERN_EXIT_TYPE_OPTIONS : EXIT_TYPE_OPTIONS), [employmentType]);
    // If employmentType flips to/from Internship and the currently selected
    // exitType isn't valid for the new option set (e.g. "Retirement" was
    // picked, then employmentType changed to Internship), clear it rather
    // than silently submitting a value that isn't even in the dropdown.
    useEffect(() => {
        if (exitType && !exitTypeOptions.includes(exitType)) {
            setValue("exitType", "");
        }
    }, [exitTypeOptions, exitType, setValue]);
    // Which checklist template is active — depends on both the employee's
    // employment type (Intern vs everyone else) and the selected exit type.
    const checklistDefs = useMemo(() => getChecklistDefs(employmentType, exitType), [employmentType, exitType]);
    const totalTasks = useMemo(() => checklistDefs.reduce((s, l) => s + l.items.length, 0), [checklistDefs]);
    // The active template's shape changes with employment/exit type, so any
    // ticks recorded against the previous template's indices no longer mean
    // anything — reset whenever the template itself changes.
    useEffect(() => {
        setCheckStates(checklistDefs.map((l) => l.items.map(() => false)));
    }, [checklistDefs]);
    const totalChecked = checkStates.flat().filter(Boolean).length;
    const progress = totalTasks > 0 ? Math.round((totalChecked / totalTasks) * 100) : 0;
    // WHO exists comes from Onboarding (the employee master). WHAT
    // departments/designations exist comes from the actual Dept &
    // Designation Master — kept separate on purpose, since a new department
    // can be created there before anyone is ever hired into it.
    useEffect(() => {
        axios
            .get(`${API_BASE}/onboarding/employee-master`)
            .then((res) => setMaster(res.data?.data ?? { employees: [] }))
            .catch(() => toast.error("Failed to load employee master data"));
        axios
            .get(`${API_BASE}/rolemaster/all`)
            .then((res) => setDeptDesig({
            departments: res.data?.data?.departments ?? [],
            designations: res.data?.data?.designations ?? [],
        }))
            .catch(() => toast.error("Failed to load department/designation master"));
    }, []);
    const currentEmployees = useMemo(() => master.employees.filter((e) => e.is_current), [master.employees]);
    // Set for the one dept-change caused by selecting an employee below (so
    // that change doesn't wipe the designation the SAME selection just
    // auto-filled) — never for a genuine manual department change by the
    // user, which should still clear designation as before.
    const skipDesignationResetRef = useRef(false);
    useEffect(() => {
        if (skipDesignationResetRef.current) {
            skipDesignationResetRef.current = false;
            return;
        }
        setValue("designation", "");
    }, [selectedDept, setValue]);
    // Auto-fill employee basic details, official data, and joining data from
    // the matched Onboarding record when picking an existing employee.
    useEffect(() => {
        const match = currentEmployees.find((e) => e.full_name === selectedName);
        if (match) {
            // Basic details
            if (match.gender)
                setValue("gender", match.gender);
            if (match.mobile)
                setValue("mobile", match.mobile);
            if (match.personal_email)
                setValue("persEmail", match.personal_email);
            setValue("officialEmail", match.official_email || match.email || "");
            // Official data
            if (match.department) {
                if (match.designation)
                    skipDesignationResetRef.current = true;
                setValue("dept", match.department);
            }
            if (match.designation)
                setValue("designation", match.designation);
            const mappedEmploymentType = mapEmployeeCategoryToEmploymentType(match.employee_category);
            if (mappedEmploymentType)
                setValue("employmentType", mappedEmploymentType);
            // Joining data
            setJoiningDate(match.joining_date ? dayjs(match.joining_date) : null);
        }
        else {
            setJoiningDate(null);
        }
    }, [selectedName, currentEmployees, setValue]);
    const filteredDesignations = useMemo(() => {
        const base = deptDesig.designations.filter((item) => {
            const dept = (item.department || item.Department || "").trim().toLowerCase();
            return dept === (selectedDept || "").trim().toLowerCase();
        });
        // The employee master's designation text (free text, set independently
        // at onboarding time) can drift from Role Master's own list for this
        // department — a rename in Role Master after the fact, or an older/
        // imported record that never actually went through this dropdown.
        // Without this, auto-filling a designation Role Master doesn't
        // currently have listed for this department leaves the <select> with
        // no matching <option> — the browser just renders it blank even
        // though the real value is set correctly underneath. Add it as a
        // selectable option so what got auto-filled is always visible.
        const hasMatch = base.some((d) => d.designation === selectedDesignation);
        if (selectedDesignation && !hasMatch) {
            return [
                ...base,
                { department: selectedDept || "", desig_id: "current", designation: selectedDesignation },
            ];
        }
        return base;
    }, [deptDesig.designations, selectedDept, selectedDesignation]);
    // CC and "transfer knowledge to" should only offer currently employed
    // people — not exited employees, and not the old stale sheet's roster.
    const ccOptions = currentEmployees
        .filter((employee) => employee.official_email)
        .map((employee) => ({
        value: employee.official_email,
        label: employee.full_name,
    }));
    const toggleCheck = (listIdx, itemIdx) => {
        setCheckStates((prev) => {
            const next = prev.map((l) => [...l]);
            next[listIdx][itemIdx] = !next[listIdx][itemIdx];
            return next;
        });
    };
    const resetFormState = () => {
        reset();
        setCheckStates(checklistDefs.map((l) => l.items.map(() => false)));
        setResignationDate(null);
        setPlannedExitDate(null);
        setLeftDate(null);
        setJoiningDate(null);
        setEmployeesInCc([]);
    };
    const onSubmit = async (data) => {
        try {
            const payload = {
                ...data,
                resignationDate: resignationDate?.toISOString(),
                plannedExitDate: plannedExitDate?.toISOString(),
                leftDate: leftDate?.toISOString(),
                joiningDate: joiningDate?.toISOString(),
                employeesInCc,
                checkLists: checklistDefs.map((listDef, listIdx) => ({
                    name: listDef.name,
                    items: listDef.items.map((_, itemIdx) => ({
                        checked: checkStates[listIdx][itemIdx],
                        name: "new",
                    })),
                })),
            };
            await axios.post(`${API_BASE}/exit`, payload);
            // Show the thank-you screen (loader first, then confirmation)
            // instead of resetting the form immediately.
            setShowThankYou(true);
            setThankYouLoading(true);
            setTimeout(() => setThankYouLoading(false), 1200);
        }
        catch (err) {
            toast.error(err?.response?.data?.message ?? "Submission failed");
        }
    };
    const handleThankYouDone = () => {
        setShowThankYou(false);
        resetFormState();
    };
    // ─── Field helpers ──────────────────────────────────────────────────────
    const inputClass = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none transition";
    const labelClass = "block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide";
    const errorClass = "text-red-500 text-xs mt-0.5";
    const sectionTitle = (title, subtitle) => (_jsxs("div", { className: "mb-5", children: [_jsx("h3", { className: "text-base font-bold text-slate-800", children: title }), subtitle && _jsx("p", { className: "text-xs text-slate-500 mt-0.5", children: subtitle })] }));
    return (_jsxs(Box, { sx: { display: "flex" }, children: [_jsx(Sidebar, {}), _jsxs(Box, { sx: { flexGrow: 1 }, children: [_jsx(Navbar, {}), _jsx(LocalizationProvider, { dateAdapter: AdapterDayjs, children: _jsxs("div", { className: "min-h-screen", style: { marginTop: "56px", background: '#F3F5EF' }, children: [_jsx("div", { className: "bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm", children: _jsxs("div", { className: "max-w-5xl mx-auto px-6 py-4 flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-xl font-bold text-slate-900", children: "New Exit" }), _jsx("p", { className: "text-xs text-slate-500 mt-0.5", children: "Enter new exit details" })] }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsxs("div", { className: "text-right", children: [_jsx("p", { className: "text-xs text-slate-500", children: "Tasks completed" }), _jsxs("p", { className: "text-sm font-bold text-red-600", children: [totalChecked, " / ", totalTasks] })] }), _jsx("div", { className: "w-32", children: _jsx(LinearProgress, { variant: "determinate", value: progress, sx: {
                                                                height: 8,
                                                                borderRadius: 4,
                                                                backgroundColor: "#e2e8f0",
                                                                "& .MuiLinearProgress-bar": { backgroundColor: "#dc2626" },
                                                            } }) })] })] }) }), _jsxs("form", { onSubmit: handleSubmit(onSubmit), className: "max-w-5xl mx-auto px-6 py-8 space-y-8", children: [_jsxs("section", { className: "bg-white rounded-2xl border border-slate-200 p-6 shadow-sm", children: [sectionTitle("Employee Basic Data"), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: [_jsxs("div", { className: "md:col-span-2", children: [_jsx("label", { className: labelClass, children: "Name of Exiting Employee *" }), _jsxs("select", { ...register("name"), className: inputClass, children: [_jsx("option", { value: "", children: "Select employee" }), currentEmployees.map((employee) => (_jsx("option", { value: employee.full_name, children: employee.full_name }, employee.employee_id || employee.full_name)))] }), errors.name && _jsx("p", { className: errorClass, children: errors.name.message })] }), _jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Gender *" }), _jsxs("select", { ...register("gender"), className: inputClass, children: [_jsx("option", { value: "", children: "Select" }), _jsx("option", { value: "Male", children: "Male" }), _jsx("option", { value: "Female", children: "Female" }), _jsx("option", { value: "Other", children: "Other" })] }), errors.gender && _jsx("p", { className: errorClass, children: errors.gender.message })] }), _jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Mobile *" }), _jsx("input", { ...register("mobile"), className: inputClass, placeholder: "+91 9999999999" }), errors.mobile && _jsx("p", { className: errorClass, children: errors.mobile.message })] }), _jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Personal Email *" }), _jsx("input", { ...register("persEmail"), className: inputClass, placeholder: "personal@gmail.com" }), errors.persEmail && _jsx("p", { className: errorClass, children: errors.persEmail.message })] }), _jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Official Email" }), _jsx("input", { ...register("officialEmail"), className: inputClass, placeholder: "name@company.com" })] })] })] }), _jsxs("section", { className: "bg-white rounded-2xl border border-slate-200 p-6 shadow-sm", children: [sectionTitle("Official Data"), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Department *" }), _jsxs("select", { ...register("dept"), className: inputClass, children: [_jsx("option", { value: "", children: "Select department" }), deptDesig.departments.map((dept) => (_jsx("option", { value: dept.department, children: dept.department }, `${dept.dept_id}-${dept.department}`)))] }), errors.dept && _jsx("p", { className: errorClass, children: errors.dept.message })] }), _jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Designation *" }), _jsxs("select", { ...register("designation"), value: selectedDesignation ?? "", className: inputClass, disabled: !selectedDept, children: [_jsx("option", { value: "", children: selectedDept ? "Select designation" : "Select department first" }), filteredDesignations.map((designation) => (_jsx("option", { value: designation.designation, children: designation.designation }, `${designation.department}-${designation.desig_id}-${designation.designation}`)))] }), errors.designation && _jsx("p", { className: errorClass, children: errors.designation.message })] }), _jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Notice Period *" }), _jsx("input", { ...register("noticePeriod"), className: inputClass, placeholder: "e.g. 30 Days" }), errors.noticePeriod && _jsx("p", { className: errorClass, children: errors.noticePeriod.message })] }), _jsxs("div", { children: [_jsx("label", { className: labelClass, children: "You Will Transfer Knowledge To *" }), _jsxs("select", { ...register("transferKnowledge"), className: inputClass, children: [_jsx("option", { value: "", children: "Select employee" }), currentEmployees.map((employee) => (_jsx("option", { value: employee.full_name, children: employee.full_name }, employee.employee_id || employee.full_name)))] }), errors.transferKnowledge && _jsx("p", { className: errorClass, children: errors.transferKnowledge.message })] }), _jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Type of Employment" }), _jsxs("select", { ...register("employmentType"), className: inputClass, children: [_jsx("option", { value: "", children: "Select employment type" }), EMPLOYMENT_TYPE_OPTIONS.map((option) => (_jsx("option", { value: option, children: option }, option)))] }), _jsx("p", { className: "text-[11px] text-slate-400 mt-1", children: "Auto-filled from Onboarding; override if needed." })] }), _jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Date of Joining" }), _jsx(DatePicker, { value: joiningDate, onChange: setJoiningDate, disabled: true, slotProps: { textField: { size: "small", fullWidth: true } } })] })] })] }), _jsxs("section", { className: "bg-white rounded-2xl border border-slate-200 p-6 shadow-sm", children: [sectionTitle("Exit Timeline"), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Employee / Company Submitted Resignation Mail On" }), _jsx(DatePicker, { value: resignationDate, onChange: setResignationDate, slotProps: { textField: { size: "small", fullWidth: true } } })] }), _jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Exit Status *" }), _jsxs("select", { ...register("exitStatus"), className: inputClass, children: [_jsx("option", { value: "", children: "Select Exit Status" }), _jsx("option", { value: "Serving Notice Period", children: "Serving Notice Period" }), _jsx("option", { value: "Already Left", children: "Already Left" }), _jsx("option", { value: "Left", children: "Left" }), _jsx("option", { value: "Not Exiting", children: "Not Exiting" }), _jsx("option", { value: "Exit Cancelled", children: "Exit Cancelled" })] }), errors.exitStatus && _jsx("p", { className: errorClass, children: errors.exitStatus.message })] }), _jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Type of Exit *" }), _jsxs("select", { ...register("exitType"), className: inputClass, children: [_jsx("option", { value: "", children: "Select Type of Exit" }), exitTypeOptions.map((option) => (_jsx("option", { value: option, children: option }, option)))] }), errors.exitType && _jsx("p", { className: errorClass, children: errors.exitType.message })] }), exitStatus === "Serving Notice Period" && (_jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Planned Exit Date" }), _jsx(DatePicker, { value: plannedExitDate, onChange: setPlannedExitDate, slotProps: { textField: { size: "small", fullWidth: true } } })] })), ["Already Left", "Left"].includes(exitStatus || "") && (_jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Left Date" }), _jsx(DatePicker, { value: leftDate, onChange: setLeftDate, slotProps: { textField: { size: "small", fullWidth: true } } })] }))] }), exitType === "Asked to Leave" && (_jsxs("div", { className: "mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-4", children: [_jsx("p", { className: "text-xs font-semibold text-amber-800 uppercase tracking-wide", children: "Confidential \u2014 never included in any exit email" }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Reason Would Have Been Asked to Leave (Confidential) *" }), _jsxs("select", { ...register("reasonAskedToLeave"), className: inputClass, children: [_jsx("option", { value: "", children: "Select reason" }), REASON_ASKED_TO_LEAVE_OPTIONS.map((option) => (_jsx("option", { value: option, children: option }, option)))] }), errors.reasonAskedToLeave && (_jsx("p", { className: errorClass, children: errors.reasonAskedToLeave.message }))] }), _jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Reasons (in Detail) (Confidential)" }), _jsx("input", { ...register("reasonAskedToLeaveDetail"), className: inputClass, placeholder: "Add confidential detail..." })] })] })] }))] }), _jsxs("section", { className: "bg-white rounded-2xl border border-slate-200 p-6 shadow-sm", children: [sectionTitle("Remarks & Email CC"), _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Remarks" }), _jsx("input", { ...register("remarks"), className: inputClass, placeholder: "Any additional notes..." })] }), _jsxs("div", { className: "rounded-lg bg-red-50 border border-red-100 px-3 py-2.5 text-xs text-red-700", children: ["This email will be sent including ", _jsx("span", { className: "font-semibold", children: "MD, CEO, COO, HR, Accounts & DME" }), ". Select others to keep in CC."] }), _jsxs("div", { children: [_jsx("label", { className: labelClass, children: "Keep in Email CC" }), _jsx(Select, { isMulti: true, options: ccOptions, onChange: (selected) => setEmployeesInCc(selected.map((s) => s.value)), placeholder: "Select employees...", classNamePrefix: "react-select", styles: {
                                                                        control: (base) => ({
                                                                            ...base,
                                                                            borderColor: "#e2e8f0",
                                                                            borderRadius: "0.5rem",
                                                                            minHeight: "42px",
                                                                            boxShadow: "none",
                                                                            "&:hover": { borderColor: "#dc2626" },
                                                                        }),
                                                                    } })] })] })] }), _jsxs("section", { className: "bg-white rounded-2xl border border-slate-200 p-6 shadow-sm", children: [sectionTitle("Automated Emails", "Tick to send auto-emails on form submission"), _jsx("div", { className: "space-y-2", children: [
                                                        { id: "autoExitEmail", label: "Send Auto Resignation Acceptance Email to Employee?" },
                                                        { id: "autoExitEmailDept", label: "Send Auto Resignation Info to All Departments?" },
                                                        { id: "autoReminderEmail", label: "Send Auto Reminder Email Before Last Date?" },
                                                        { id: "autoInstructionsToAllEmail", label: "Send Auto-Instructions to All Departments?" },
                                                    ].map(({ id, label }) => (_jsx(Controller, { name: id, control: control, render: ({ field }) => (_jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: !!field.value, onChange: field.onChange, sx: { color: "#dc2626", "&.Mui-checked": { color: "#dc2626" } } }), label: _jsx("span", { className: "text-sm text-slate-700", children: label }) })) }, id))) })] }), _jsxs("section", { className: "bg-white rounded-2xl border border-slate-200 p-6 shadow-sm", children: [sectionTitle("Exit Checklists", "Tick tasks as they are completed"), _jsxs("div", { className: "mb-6 p-4 rounded-xl bg-red-50 border border-red-100 flex items-center gap-4", children: [_jsx("div", { className: "flex-1", children: _jsx(LinearProgress, { variant: "determinate", value: progress, sx: {
                                                                    height: 10,
                                                                    borderRadius: 5,
                                                                    backgroundColor: "#fecaca",
                                                                    "& .MuiLinearProgress-bar": { backgroundColor: "#dc2626" },
                                                                } }) }), _jsx(Chip, { label: `${progress}%`, size: "small", sx: { backgroundColor: "#dc2626", color: "#fff", fontWeight: 700 } })] }), _jsx("div", { className: "space-y-3", children: checklistDefs.map((listDef, listIdx) => (_jsxs(Accordion, { defaultExpanded: listIdx === 0, sx: {
                                                            borderRadius: "12px !important",
                                                            border: "1px solid #e2e8f0",
                                                            boxShadow: "none",
                                                            "&:before": { display: "none" },
                                                            overflow: "hidden",
                                                        }, children: [_jsx(AccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, {}), children: _jsxs("div", { className: "flex items-center gap-3 w-full pr-2", children: [_jsx("span", { className: "w-2.5 h-2.5 rounded-full flex-shrink-0", style: { backgroundColor: listDef.accent } }), _jsx("span", { className: "font-semibold text-slate-700 text-sm", children: listDef.name }), _jsxs("span", { className: "ml-auto text-xs text-slate-400", children: [(checkStates[listIdx] ?? []).filter(Boolean).length, " / ", listDef.items.length] })] }) }), _jsx(AccordionDetails, { className: `${listDef.color} !pt-0`, children: _jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-1 pt-2", children: listDef.items.map((itemLabel, itemIdx) => (_jsxs("label", { className: "flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/60 cursor-pointer transition-colors", children: [_jsx("input", { type: "checkbox", checked: checkStates[listIdx]?.[itemIdx] ?? false, onChange: () => toggleCheck(listIdx, itemIdx), className: "w-4 h-4 rounded accent-red-600 cursor-pointer" }), _jsx("span", { className: `text-sm ${checkStates[listIdx]?.[itemIdx]
                                                                                    ? "line-through text-slate-400"
                                                                                    : "text-slate-700"}`, children: itemLabel })] }, itemIdx))) }) })] }, listDef.name))) })] }), _jsxs("div", { className: "flex justify-end gap-3 pb-8", children: [_jsx("button", { type: "button", onClick: () => {
                                                        reset();
                                                        setCheckStates(checklistDefs.map((l) => l.items.map(() => false)));
                                                    }, className: "px-6 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition", children: "Reset" }), _jsx("button", { type: "submit", disabled: isSubmitting, className: "px-8 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold shadow transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2", children: isSubmitting ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" }), "Submitting..."] })) : ("Submit Exit") })] })] })] }) }), showThankYou && (_jsx("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4", children: _jsx("div", { className: "bg-white rounded-2xl shadow-2xl p-10 max-w-md w-full text-center", children: thankYouLoading ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "w-14 h-14 mx-auto mb-5 border-4 border-red-100 border-t-red-600 rounded-full animate-spin" }), _jsx("h2", { className: "text-lg font-bold text-slate-700", children: "Submitting Exit\u2026" }), _jsx("p", { className: "text-sm text-slate-400 mt-1", children: "Just a moment" })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center", children: _jsx("svg", { className: "w-8 h-8 text-green-600", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 3, children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M5 13l4 4L19 7" }) }) }), _jsx("h2", { className: "text-2xl font-bold text-slate-800 mb-2", children: "Thank You!" }), _jsx("p", { className: "text-sm text-slate-500 mb-6", children: "Exit entry has been submitted successfully." }), _jsx("button", { onClick: handleThankYouDone, className: "px-8 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold shadow transition", children: "Add Another Exit Entry" })] })) }) }))] })] }));
};
export default NewExit;
